import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MessageSquare } from "lucide-react";
import {
  taskDiscussionService,
  apiErrorMessage,
} from "../../api/taskBoardService";
import ActivityItem from "./ActivityItem";
import CommentComposer from "./CommentComposer";
import { ImagePreviewModal } from "./attachments";
import { roleIn } from "./boardState";
import { EmptyState, ErrorState } from "./shared";
import {
  applyAttachmentUpdates,
  applyCommentUpdate,
  groupByDay,
  matchesFeedFilter,
  mergeFeed,
} from "./activityFeed";

const TABS = [
  { value: "all", label: "All" },
  { value: "comments", label: "Comments" },
  { value: "history", label: "History" },
  { value: "files", label: "Files" },
];
const PAGE_SIZE = 30;

/**
 * Right-hand "Comments and Activity" panel of the task details (Trello-style):
 * composer on top, then the newest-first feed grouped by day, loaded in pages.
 * Kept live by the task's realtime activity events (deduped by id), and
 * re-fetched after a reconnect.
 */
const TaskActivityPanel = ({ task, access, subscribeActivity }) => {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [mentionable, setMentionable] = useState([]);
  const [preview, setPreview] = useState(null);
  // Collapses the tabs + activity list (the composer stays).
  const [showActivity, setShowActivity] = useState(true);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const loadFirstPage = useCallback(async () => {
    const requested = filter;
    try {
      const res = await taskDiscussionService.activity(task._id, {
        filter: requested,
        limit: PAGE_SIZE,
      });
      if (filterRef.current !== requested) return;
      // Keep anything newer that arrived live while loading.
      setItems((prev) =>
        mergeFeed(
          prev.filter((i) => matchesFeedFilter(i, requested)),
          res.data,
        ),
      );
      setCursor(res.meta?.nextCursor || null);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load activity"));
    } finally {
      setLoading(false);
    }
  }, [task._id, filter]);

  useEffect(() => {
    setItems([]);
    setLoading(true);
    loadFirstPage();
  }, [loadFirstPage]);

  useEffect(() => {
    taskDiscussionService
      .mentionable(task._id)
      .then((res) => setMentionable(res.data))
      .catch(() => setMentionable([]));
  }, [task._id]);

  const loadOlder = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await taskDiscussionService.activity(task._id, {
        filter,
        before: cursor,
        limit: PAGE_SIZE,
      });
      setItems((prev) => mergeFeed(prev, res.data));
      setCursor(res.meta?.nextCursor || null);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Could not load older activity"));
    } finally {
      setLoadingMore(false);
    }
  };

  // Live updates for this task.
  useEffect(
    () =>
      subscribeActivity?.((event) => {
        if (event.type === "resync") return loadFirstPage();
        if (event.taskId !== task._id) return;
        setItems((prev) => {
          let next = mergeFeed(
            prev,
            (event.activities || []).filter((a) =>
              matchesFeedFilter(a, filterRef.current),
            ),
          );
          if (event.comment) next = applyCommentUpdate(next, event.comment);
          if (event.attachments?.length)
            next = applyAttachmentUpdates(next, event.attachments);
          return next;
        });
      }),
    [subscribeActivity, task._id, loadFirstPage],
  );

  const people = useMemo(
    () => new Map(mentionable.map((p) => [p._id, p])),
    [mentionable],
  );
  const groups = useMemo(() => groupByDay(items), [items]);

  const addComment = async (draft, onUploadProgress) => {
    try {
      const res = await taskDiscussionService.addComment(
        task._id,
        draft,
        onUploadProgress,
      );
      if (matchesFeedFilter(res.data, filterRef.current))
        setItems((prev) => mergeFeed(prev, [res.data]));
    } catch (err) {
      throw new Error(apiErrorMessage(err, "Could not send the comment"));
    }
  };

  const editComment = async (commentId, body, mentions) => {
    try {
      const res = await taskDiscussionService.editComment(
        commentId,
        body,
        mentions,
      );
      setItems((prev) => applyCommentUpdate(prev, res.data));
    } catch (err) {
      throw new Error(apiErrorMessage(err, "Could not save the comment"));
    }
  };

  const deleteComment = async (comment) => {
    try {
      const res = await taskDiscussionService.deleteComment(comment._id);
      setItems((prev) => applyCommentUpdate(prev, res.data));
    } catch (err) {
      setActionError(apiErrorMessage(err, "Could not delete the comment"));
    }
  };

  const removeFile = async (attachment) => {
    const res = await taskDiscussionService.removeAttachment(attachment);
    setItems((prev) => applyAttachmentUpdates(prev, [res.data]));
  };

  const canRemoveFile = (a) =>
    a.uploadedBy === access.userId ||
    access.isAdmin ||
    roleIn(access, task.squad) === "SQUAD_LEAD";

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2.5 text-[15.5px] font-semibold text-ink whitespace-nowrap">
          <MessageSquare className="w-[18px] h-[18px] text-muted" /> Comments and Activity
        </h3>
        <button
          type="button"
          onClick={() => setShowActivity((v) => !v)}
          aria-expanded={showActivity}
          className="btn-ghost !h-8 !px-3 text-[12.5px]"
        >
          {showActivity ? "Hide" : "Show"}
        </button>
      </div>

      <CommentComposer
        mentionable={mentionable}
        onSubmit={addComment}
        submitLabel="Comment"
      />

      {showActivity && (
      <>
      <div
        className="flex gap-1 p-1 rounded-xl bg-raise"
        role="tablist"
        aria-label="Activity filter"
      >
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={filter === t.value}
            onClick={() => setFilter(t.value)}
            className={`flex-1 text-[12.5px] font-medium px-2 py-1.5 rounded-lg transition-colors ${
              filter === t.value ? "bg-panel-solid text-ink shadow-card" : "text-muted hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {actionError && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">
          {actionError}
        </p>
      )}

      {error ? (
        <ErrorState message={error} onRetry={loadFirstPage} />
      ) : loading ? (
        <div className="space-y-3 animate-pulse" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-dark-300" />
              <div className="flex-1 h-12 rounded-lg bg-gray-200 dark:bg-dark-300" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title={
            filter === "comments"
              ? "No comments yet"
              : filter === "files"
                ? "No files yet"
                : "No activity yet"
          }
          message={
            filter === "comments" ? "Start the conversation above." : undefined
          }
        />
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.key}>
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-faint mb-2">
                {g.label}
              </h4>
              <ul className="space-y-4">
                {g.items.map((item) => (
                  <ActivityItem
                    key={item._id}
                    item={item}
                    access={access}
                    people={people}
                    mentionable={mentionable}
                    canRemoveFile={canRemoveFile}
                    onEditComment={editComment}
                    onDeleteComment={deleteComment}
                    onRemoveFile={removeFile}
                    onPreview={setPreview}
                    onError={(err) =>
                      setActionError(apiErrorMessage(err, "Action failed"))
                    }
                  />
                ))}
              </ul>
            </section>
          ))}
          {cursor && (
            <button
              type="button"
              onClick={loadOlder}
              disabled={loadingMore}
              className="w-full text-sm font-medium py-2 rounded-xl text-muted hover:bg-hover disabled:opacity-50"
            >
              {loadingMore ? "Loading…" : "Show older activity"}
            </button>
          )}
        </div>
      )}

      </>
      )}

      <ImagePreviewModal
        attachment={preview}
        onClose={() => setPreview(null)}
      />
    </div>
  );
};

export default TaskActivityPanel;
